import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './json-to-types.component.module.scss';

type Target = 'typescript' | 'go' | 'python' | 'rust' | 'kotlin' | 'java' | 'csharp' | 'jsonschema';

const TARGET_LABEL: Record<Target, string> = {
    typescript: 'TypeScript',
    go: 'Go',
    python: 'Python',
    rust: 'Rust',
    kotlin: 'Kotlin',
    java: 'Java',
    csharp: 'C#',
    jsonschema: 'JSON Schema',
};

const SLUG_TO_TARGET: Record<string, Target> = {
    'convert-json-to-typescript': 'typescript',
    'convert-json-to-go': 'go',
    'convert-json-to-python': 'python',
    'convert-json-to-rust': 'rust',
    'convert-json-to-kotlin': 'kotlin',
    'convert-json-to-java': 'java',
    'convert-json-to-csharp': 'csharp',
    'convert-json-to-jsonschema': 'jsonschema',
};

const SAMPLE = `{
  "user": {
    "id": 42,
    "name": "Ada Lovelace",
    "email": "ada@example.com",
    "isActive": true,
    "tags": ["math", "computing"],
    "joined": "1843-12-10"
  }
}`;

type LibState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; mod: any }
    | { status: 'error'; message: string };

interface State {
    target: Target;
    rootName: string;
    input: string;
    output: string;
    processing: boolean;
    lib: LibState;
    error: string | null;
    copied: boolean;
}

export default class JsonToTypesComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const target = SLUG_TO_TARGET[props.feature || ''] || 'typescript';
        this.state = {
            target,
            rootName: 'Root',
            input: SAMPLE,
            output: '',
            processing: false,
            lib: { status: 'idle' },
            error: null,
            copied: false,
        };
    }

    private async ensureLibLoaded(): Promise<any | null> {
        if (this.state.lib.status === 'ready') return this.state.lib.mod;
        if (this.state.lib.status === 'loading') return null;
        this.setState({ lib: { status: 'loading' } });
        try {
            const mod = await import('quicktype-core');
            this.setState({ lib: { status: 'ready', mod } });
            return mod;
        } catch (e) {
            this.setState({ lib: { status: 'error', message: 'Failed to load type generator. Check your connection and refresh.' } });
            return null;
        }
    }

    handleConvert = async () => {
        const mod = await this.ensureLibLoaded();
        if (!mod) return;
        this.setState({ processing: true, error: null });
        try {
            const { quicktype, InputData, jsonInputForTargetLanguage } = mod;
            const jsonInput = jsonInputForTargetLanguage(this.state.target);
            await jsonInput.addSource({
                name: this.state.rootName || 'Root',
                samples: [this.state.input],
            });
            const inputData = new InputData();
            inputData.addInput(jsonInput);
            const result = await quicktype({
                inputData,
                lang: this.state.target,
                rendererOptions: this.state.target === 'typescript' ? { 'just-types': 'true' } : {},
            });
            const output = result.lines.join('\n');
            this.setState({ output, processing: false, copied: false });
        } catch (e: any) {
            this.setState({
                processing: false,
                output: '',
                error: e?.message || 'Failed to generate types. Check that your input is valid JSON.',
            });
        }
    };

    private setTarget = (target: Target) => {
        this.setState({ target, output: '', error: null });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { target, rootName, input, output, processing, lib, error, copied } = this.state;
        const buttonState =
            lib.status === 'error' ? { disabled: true, text: 'Load failed' } :
            lib.status === 'loading' ? { disabled: true, text: 'Loading…' } :
            processing ? { disabled: true, text: 'Generating…' } :
            { disabled: false, text: 'Generate' };

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.targetTabs} role="tablist" aria-label="Output language">
                            {(Object.keys(TARGET_LABEL) as Target[]).map(t => (
                                <button key={t}
                                    role="tab"
                                    aria-selected={target === t}
                                    className={`${styles.targetTab} ${target === t ? styles.targetTabActive : ''}`}
                                    onClick={() => this.setTarget(t)}>
                                    {TARGET_LABEL[t]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Root type name
                            <input className={styles.input} type="text" value={rootName}
                                onChange={e => this.setState({ rootName: e.target.value || 'Root' })} />
                        </label>
                        <label className={sharedStyles.label}>
                            JSON input
                            <textarea className={sharedStyles.textarea} rows={16} value={input}
                                spellCheck={false}
                                onChange={e => this.setState({ input: e.target.value })}
                                aria-label="JSON input" />
                        </label>
                        {error && <p className={styles.error} role="alert">{error}</p>}
                        {lib.status === 'error' && <p className={styles.error} role="alert">{lib.message}</p>}
                        <button className={sharedStyles.buttonPrimary} onClick={this.handleConvert}
                            disabled={buttonState.disabled} style={{ marginTop: '0.5rem' }}>
                            {buttonState.text}
                        </button>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>{TARGET_LABEL[target]} output</label>
                        <textarea className={sharedStyles.textarea} readOnly rows={20}
                            value={output} placeholder="Click Generate to produce types from your JSON."
                            aria-label="Generated type output" />
                        <div className={styles.actions}>
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput} disabled={!output}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
